import React, { useState, useEffect, useCallback } from 'react';
import { btnStyle, selectStyle } from './PlateStyles';
import { ispeglaVreme, izracunajSate } from './PlateUtils';
import PlateTable from './PlateTable';
import ImportDatModal from './Modals/ImportDatModal';
import DatabaseModal from './Modals/DatabaseModal';
import AddRowModal from './Modals/AddRowModal';
import DeleteConfirmModal from './Modals/DeleteConfirmModal';
import PlateTableSuma from './PlateTableSuma';

const Plate = () => {
    // --- STATE-OVI ---
    const [sviRadniciPodaci, setSviRadniciPodaci] = useState({}); 
    const [listaSvihRadnika, setListaSvihRadnika] = useState([]);
    const [punPodatakORadnicima, setPunPodatakORadnicima] = useState([]); // Dodato za cene
    const [selektovaniRadnik, setSelektovaniRadnik] = useState('');
    const [tabelaDana, setTabelaDana] = useState({});
    const [showLogModal, setShowLogModal] = useState(false);
    const [showBazaModal, setShowBazaModal] = useState(false);
    const [showAddRowModal, setShowAddRowModal] = useState(false);
    const [newRowDate, setNewRowDate] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [dateRange, setDateRange] = useState({ od: '', do: '' });
    const [statusPoruka, setStatusPoruka] = useState('');
    const [modalStatus, setModalStatus] = useState('');
    const [rucniStatusi, setRucniStatusi] = useState({});
    const [terenStatusi, setTerenStatusi] = useState({});
    const [terenskiDodaci, setTerenskiDodaci] = useState({});
    const [rucnaVremena, setRucnaVremena] = useState({}); 
    const [obrisaniDatumi, setObrisaniDatumi] = useState({});
    const [zakljucaniRedovi, setZakljucaniRedovi] = useState({});
    const [tabelaZakljucana, setTabelaZakljucana] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ show: false, datum: null });

    // --- POMOĆNA FUNKCIJA ZA PROVERU TRAJANJA ---
    const parseTimeToDecimal = (timeStr) => {
        if (!timeStr || typeof timeStr !== 'string' || !timeStr.includes(':')) return 0;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours + (minutes / 60);
    };

    // --- INICIJALIZACIJA ---
    useEffect(() => {
        const danas = new Date();
        const prviDanPrethodnog = new Date(danas.getFullYear(), danas.getMonth() - 1, 1);
        const poslednjiDanPrethodnog = new Date(danas.getFullYear(), danas.getMonth(), 0);

        const format = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const dan = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${dan}`;
        };

        const initialOd = format(prviDanPrethodnog);
        const initialDo = format(poslednjiDanPrethodnog);

        setDateRange({ od: initialOd, do: initialDo });
        setNewRowDate(initialDo);

        fetch('https://ecopell.ledscreen.rs/api/workers/')
            .then(res => res.json())
            .then(data => {
                setPunPodatakORadnicima(data); 
                const sorted = data.map(r => ({ id: r.id.toString().trim(), imePrezime: `${r.ime} ${r.prezime}` }))
                                   .sort((a, b) => a.imePrezime.localeCompare(b.imePrezime));
                setListaSvihRadnika(sorted);
                if (sorted.length > 0) setSelektovaniRadnik(sorted[0].imePrezime);
            });
    }, []);

    // --- API POZIVI ---
    const povuciIzBazeZaRadnika = async () => {
        setModalStatus('Provera baze...');
        try {
            const res = await fetch(`https://ecopell.ledscreen.rs/api/get-plate-data/?radnik=${selektovaniRadnik}&od=${dateRange.od}&do=${dateRange.do}`);
            const result = await res.json();
            if (res.ok && result.podaci) {
                const nV = {}; const nS = {}; const nT = {}; const nD = {}; const nZ = {};
                Object.keys(result.podaci).forEach(datum => {
                    const d = result.podaci[datum];
                    nV[`${datum}_p`] = d.prijava || '00:00';
                    nV[`${datum}_o`] = d.odjava || '00:00';
                    nS[datum] = d.status || 'Nema zapisa';
                    nT[datum] = d.teren || 'ne';
                    nD[datum] = d.teren_dodatak || 'ne';
                    nZ[datum] = d.zakljucan;
                });
                setRucnaVremena(nV); setRucniStatusi(nS); setTerenStatusi(nT); setTerenskiDodaci(nD); setZakljucaniRedovi(nZ);
                setTabelaZakljucana(result.tabela_zakljucana || false);
                setModalStatus('Sinhronizovano.');
            }
        } catch (e) { setModalStatus('Greška.'); }
    };

    const handleImportSubmit = async () => {
        if (!selectedFile) { alert("Izaberite fajl!"); return; }
        setModalStatus('Uvoz podataka...');
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('datum_od', dateRange.od);
        formData.append('datum_do', dateRange.do);

        try {
            const response = await fetch('https://ecopell.ledscreen.rs/api/upload-logs/', {
                method: 'POST',
                body: formData
            });
            const result = await response.json();
            if (response.ok) {
                setSviRadniciPodaci(prev => ({ ...prev, ...result.radnici_podaci }));
                await povuciIzBazeZaRadnika();
                setShowLogModal(false);
                setStatusPoruka('Podaci uvezeni.');
                setTimeout(() => setStatusPoruka(''), 3000);
            } else {
                setModalStatus('Greška: ' + (result.error || 'Neuspeh'));
            }
        } catch (err) { setModalStatus('Greška na serveru.'); }
    };

    const snimiUBazu = async () => {
        setStatusPoruka('Snimanje...');
        const podaci = pripremiPodatkeZaServer(selektovaniRadnik, tabelaDana);
        const res = await fetch('https://ecopell.ledscreen.rs/api/save-plate/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ radnik: selektovaniRadnik, tabela_zakljucana: tabelaZakljucana, podaci })
        });
        if (res.ok) setStatusPoruka('Sačuvano!');
        setTimeout(() => setStatusPoruka(''), 2000);
    };

    // --- FUNKCIJE ZA TABELU ---
    const obrisiPrazneRedove = () => {
        if (!selektovaniRadnik) return;
        const noviObrisani = { ...obrisaniDatumi };
        Object.keys(tabelaDana).forEach(datum => {
            const isRedZakljucan = tabelaZakljucana || zakljucaniRedovi[datum];
            const pV = rucnaVremena[`${datum}_p`] || tabelaDana[datum].prijava;
            const oV = rucnaVremena[`${datum}_o`] || tabelaDana[datum].odjava;
            const status = rucniStatusi[datum] || 'Nema zapisa';

            if (pV === '00:00' && oV === '00:00' && status === 'Nema zapisa' && !isRedZakljucan) {
                noviObrisani[`${selektovaniRadnik}_${datum}`] = true;
            }
        });
        setObrisaniDatumi(noviObrisani);
        setStatusPoruka('Prazni redovi uklonjeni.');
        setTimeout(() => setStatusPoruka(''), 2000);
    };

    const handleAddRow = () => {
        if (!newRowDate) return;
        const naziviDana = ["Nedelja", "Ponedeljak", "Utorak", "Sreda", "Četvrtak", "Petak", "Subota"];
        const d = new Date(newRowDate);
        setTabelaDana(prev => ({ ...prev, [newRowDate]: { prijava: '00:00', odjava: '00:00', dan: naziviDana[d.getDay()] } }));
        setObrisaniDatumi(prev => {
            const copy = { ...prev };
            delete copy[`${selektovaniRadnik}_${newRowDate}`];
            return copy;
        });
        setShowAddRowModal(false);
    };

    const handleConfirmDelete = () => {
        setObrisaniDatumi(prev => ({ ...prev, [`${selektovaniRadnik}_${deleteModal.datum}`]: true }));
        setDeleteModal({ show: false, datum: null });
    };

    const generisiTablu = useCallback((ime) => {
        if (!dateRange.od || !dateRange.do) return {};
        const dani = {};
        const naziviDana = ["Nedelja", "Ponedeljak", "Utorak", "Sreda", "Četvrtak", "Petak", "Subota"];
        const podaciRadnika = sviRadniciPodaci[ime] || [];
        let d = new Date(dateRange.od);
        let end = new Date(dateRange.do);
        while (d <= end) {
            const iso = d.toISOString().split('T')[0];
            if (!obrisaniDatumi[`${ime}_${iso}`]) {
                let prijava = rucnaVremena[`${iso}_p`] || '00:00';
                let odjava = rucnaVremena[`${iso}_o`] || '00:00';
                if (prijava === '00:00' && odjava === '00:00') {
                    const logovi = podaciRadnika.filter(l => l.datum === iso);
                    if (logovi.length > 0) {
                        const vremena = logovi[0].vreme ? logovi.map(l => l.vreme).sort() : [];
                        prijava = logovi[0].vreme ? vremena[0] : (logovi[0].prijava || '00:00');
                        odjava = logovi[0].vreme ? (vremena.length > 1 ? vremena[vremena.length-1] : '00:00') : (logovi[0].odjava || '00:00');
                    }
                }
                dani[iso] = { prijava, odjava, dan: naziviDana[d.getDay()] };
            }
            d.setDate(d.getDate() + 1);
        }
        return dani;
    }, [dateRange, sviRadniciPodaci, rucnaVremena, obrisaniDatumi]);

    useEffect(() => {
        if (selektovaniRadnik) setTabelaDana(generisiTablu(selektovaniRadnik));
    }, [selektovaniRadnik, dateRange, sviRadniciPodaci, generisiTablu]);

    const updateTime = (datum, tip, unit, value) => {
        if (tabelaZakljucana || zakljucaniRedovi[datum]) return;
        const key = `${datum}_${tip}`;
        const tren = rucnaVremena[key] || (tabelaDana[datum]?.[tip === 'p' ? 'prijava' : 'odjava'] || '00:00');
        const [h, m] = tren.split(':');
        setRucnaVremena(prev => ({...prev, [key]: unit === 'h' ? `${value}:${m}` : `${h}:${value}`}));
    };

    const pripremiPodatkeZaServer = (ime, tabela) => {
        return Object.keys(tabela)
            .filter(datum => {
                if (obrisaniDatumi[`${ime}_${datum}`]) return false;
                const podaci = tabela[datum];
                const pV_raw = rucnaVremena[`${datum}_p`] || podaci.prijava || '00:00';
                const oV_raw = rucnaVremena[`${datum}_o`] || podaci.odjava || '00:00';
                const status = rucniStatusi[datum] || 'Nema zapisa';
                const isZakljucan = zakljucaniRedovi[datum];
                if (isZakljucan) return true;
                return (pV_raw !== '00:00' || oV_raw !== '00:00') || (status !== 'Nema zapisa');
            })
            .map(datum => {
                const podaci = tabela[datum];
                const naT = terenStatusi[datum] || 'ne'; 
                const pV = ispeglaVreme(rucnaVremena[`${datum}_p`] || podaci.prijava, 'prijava');
                const oV = ispeglaVreme(rucnaVremena[`${datum}_o`] || podaci.odjava, 'odjava');
                const kalk = izracunajSate(pV, oV, naT);

                // AUTOMATIKA RADI SAMO AKO NIJE RUČNO IZABRANO "ne"
                let tDodatak = terenskiDodaci[datum]; 
                
                // Ako u memoriji (state-u) ne postoji ručni izbor (ili je null), tek onda gledaj sate
                if (tDodatak === undefined || tDodatak === null) {
                    if (kalk.ukupno && kalk.ukupno.includes(':')) {
                        const [sati, minuti] = kalk.ukupno.split(':').map(Number);
                        const ukupnoMinuta = (sati * 60) + minuti;
                        // 15:30 je 930 minuta
                        tDodatak = (ukupnoMinuta >= 930) ? 'da' : 'ne';
                    } else {
                        tDodatak = 'ne';
                    }
                }

                return {
                    datum, prijava: pV, odjava: oV, teren: naT,
                    teren_dodatak: tDodatak,
                    status: rucniStatusi[datum] || (kalk.imaSati ? 'Radni dan' : 'Nema zapisa'),
                    ukupno: kalk.ukupno, redovno: kalk.redovno, prekovremeno: kalk.prekovremeno,
                    zakljucan: !!zakljucaniRedovi[datum]
                };
            });
    };

    const toggleZakljucajRed = async (datum) => {
        const noviStatus = !zakljucaniRedovi[datum];
        setZakljucaniRedovi(prev => ({...prev, [datum]: noviStatus}));
        const podaci = pripremiPodatkeZaServer(selektovaniRadnik, tabelaDana).map(r => r.datum === datum ? {...r, zakljucan: noviStatus} : r);
        await fetch('https://ecopell.ledscreen.rs/api/save-plate/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ radnik: selektovaniRadnik, tabela_zakljucana: tabelaZakljucana, podaci })
        });
    };

    // --- PRIPREMA PODATAKA ZA SUMU ---
    const podaciZaSumu = pripremiPodatkeZaServer(selektovaniRadnik, tabelaDana);
    const izabraniRadnikObj = punPodatakORadnicima.find(r => `${r.ime} ${r.prezime}` === selektovaniRadnik);

    return (
        <div style={{padding: '20px', color: 'white', backgroundColor: '#050a1a', minHeight: '100vh'}}>
            <div style={{display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap'}}>
                <h2 style={{margin:0, fontSize: '28px'}}>Obračun Sati</h2>
                <button onClick={() => setShowLogModal(true)} style={btnStyle}>Uvoz .DAT fajla</button>
                <button onClick={() => setShowBazaModal(true)} style={{...btnStyle, backgroundColor: '#6f42c1', color: 'white'}}>Uvoz iz baze</button>
                <button onClick={obrisiPrazneRedove} style={{...btnStyle, backgroundColor: '#ffc107', color: 'black'}}>Briši prazne redove</button>
                <button onClick={() => setShowAddRowModal(true)} style={{...btnStyle, backgroundColor: '#17a2b8', color: 'white'}}>Dodaj red</button>
                <button onClick={snimiUBazu} style={{...btnStyle, backgroundColor: '#007bff', color: 'white'}}>Snimi radnika</button>
                <select value={selektovaniRadnik} onChange={(e) => setSelektovaniRadnik(e.target.value)} style={{...selectStyle, padding: '10px', fontSize: '18px', marginLeft: 'auto'}}>
                    {listaSvihRadnika.map(r => <option key={r.id} value={r.imePrezime}>{r.id} - {r.imePrezime}</option>)}
                </select>
                {statusPoruka && <span style={{color: '#00ff00', fontWeight: 'bold'}}>{statusPoruka}</span>}
            </div>

            <PlateTable 
                tabelaDana={tabelaDana} obrisaniDatumi={obrisaniDatumi} selektovaniRadnik={selektovaniRadnik}
                tabelaZakljucana={tabelaZakljucana} zakljucaniRedovi={zakljucaniRedovi}
                terenStatusi={terenStatusi} terenskiDodaci={terenskiDodaci} rucniStatusi={rucniStatusi} rucnaVremena={rucnaVremena}
                setTerenStatusi={setTerenStatusi} setTerenskiDodaci={setTerenskiDodaci} setRucniStatusi={setRucniStatusi}
                updateTime={updateTime} setDeleteModal={setDeleteModal} toggleZakljucajRed={toggleZakljucajRed} setTabelaZakljucana={setTabelaZakljucana}
            />

            <PlateTableSuma 
                tabelaDana={podaciZaSumu} 
                radnikObj={izabraniRadnikObj} 
            />

            {showLogModal && <ImportDatModal dateRange={dateRange} setDateRange={setDateRange} setSelectedFile={setSelectedFile} onImport={handleImportSubmit} onClose={() => setShowLogModal(false)} status={modalStatus} />}
            {showBazaModal && <DatabaseModal dateRange={dateRange} setDateRange={setDateRange} onFetch={povuciIzBazeZaRadnika} onClose={() => setShowBazaModal(false)} status={modalStatus} />}
            {showAddRowModal && <AddRowModal newRowDate={newRowDate} setNewRowDate={setNewRowDate} onAdd={handleAddRow} onClose={() => setShowAddRowModal(false)} />}
            {deleteModal.show && <DeleteConfirmModal datum={deleteModal.datum} onConfirm={handleConfirmDelete} onClose={() => setDeleteModal({show:false, datum:null})} />}
        </div>
    );
};

export default Plate;